import _, { isEmpty } from "lodash"
import {
  Polygon,
  MultiPolygon,
  LineString,
  Marker,
  Coordinate,
  MultiLineString,
  ui,
} from "maptalks"
import turfCenter from "@turf/center"
import turfBuffer from "@turf/buffer"
import { feature } from "@turf/helpers"

import {
  TextureLoader,
  SpriteMaterial,
  MeshLambertMaterial,
  AmbientLight,
  DirectionalLight,
} from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader"
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader"

import {
  GroundLabel,
  SpriteMarker,
  Billboard,
  NavigationPath,
} from "../object3d"
import { getCenterFromGeometry, createPolygonFromLineString } from "./geometry"
import { createSVGPathFromMarkerSymbol, svgToPng } from "./svg"
import { SUFFIX_MARKER } from "../constants"

const GeometryType = {
  Polygon,
  MultiPolygon,
}

const ORDINAL_HEIGHT = 0 // in version 3D change ORDINAL_HEIGHT for set altitude of geometry

const VENUE_Z_INDEX = 0

const LEVEL_Z_INDEX = 2
const UNIT_Z_INDEX = 3
const FIXTURE_Z_INDEX = 4
const DECORATION_Z_INDEX = 4
const FOOTPRINT_Z_INDEX = 1
const KIOSK_Z_INDEX = 5
const OPENING_Z_INDEX = 3
const SECTION_Z_INDEX = 6
const USER_LOCATION_Z_INDEX = 99
const LAST_LOCATION_Z_INDEX = USER_LOCATION_Z_INDEX - 1

const PREFIX_HIGHLIGHTED_SYMBOL_KEY = "highlighted"
const SPRITE_MARKER_FEATURE = [
  "amenity-atm",
  "amenity-babychanging",
  "amenity-strollerrental",
  "amenity-boardinggate.ferry",
  "amenity-elevator",
  "amenity-escalator",
  "amenity-stairs",
  "amenity-information",
  "amenity-information.transit",
  "amenity-wheelchair",
  "amenity-restroom",
  "amenity-restroom.male",
  "amenity-restroom.female",
  "amenity-restroom.wheelchair",
  "amenity-restroom.family",
  "amenity-restroom.unisex",
  "amenity-taxi",
  "amenity-groundtransportation",
  "amenity-bus",
  "amenity-bus.muni",
  "amenity-shuttle",
  "amenity-parking",
  "amenity-parking.shortterm",
  "amenity-parking.longterm",
  "amenity-parking.waitingarea",
  "amenity-parking.compact",
  "amenity-parking.ev",
  "amenity-parking.bicycle",
  "amenity-parking.motorcycle",
  "amenity-privatelounge",
  "amenity-rideshare",
  "amenity-valet",
  "amenity-landmark",
  "amenity-rail.muni",
  "amenity-service",
  "amenity-smokingarea",
  "amenity-ticketing",
  "amenity-meetingpoint",
  "amenity-prayerroom",
  "amenity-firstaid",
  "amenity-ticketing.rail",
  "amenity-exhibit",
  "amenity-mothersroom",
  "amenity-checkin.desk",
  "amenity-baggagestorage",
  "amenity-baggagecarts",
  "amenity-library",
  "amenity-coinlocker",
  "amenity-powerchargingstation",
  "amenity-wheelchairassit",
  "amenity-lostandfound",
  "amenity-foodservice",
  "amenity-businesscenter",
  "amenity-guestservices",
  "amenity-emergencyshelter",
  "amenity-prayerroom.buddhism",
  "amenity-defibrillator",
  "amenity-drinkingfountain",
  "amenity-handsanitizerstation",
  "amenity-unspecified",
  "amenity-entry",
  "amenity-fireextinguisher",
  "amenity-security",
  "amenity-seating",
  "amenity-copymachine",
  "amenity-fieldofplay",
  "amenity-fieldofplay.soccer",
  "amenity-amphitheater",
  "amenity-hoteling",
  "occupant-currencyexchange",
  "occupant-donationcenter",
  "occupant-bank",
  "occupant-books",
  "occupant-postoffice",
  "opening-emergencyexit",
]

const SPRITE_HIGHLIGHT_MARKER_FEATURE = SPRITE_MARKER_FEATURE.map(
  (featCate) =>
    `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${featCate.split("-").join(".")}`
)

const OCCUPANT_TEXT_MARKER_CLASSNAME = "mtk-occupant-text-marker"
const SECTION_TEXT_MARKER_CLASSNAME = "mtk-section-text-marker"

export const buildCompositeId = (uIds = [], options = {}) => {
  const { separator = "_", suffix = null, prefix = null } = options
  const combinedId = _.compact([prefix, ...uIds, suffix]).join(separator)
  return combinedId
}

export const getAltitude = (properties, modifier = ORDINAL_HEIGHT) =>
  Math.max(0, properties.ordinal * modifier || 0)

export const computeAltitudeFromOrdinals = (ordinals, modifier = 10) => {
  return _(ordinals)
    .uniq()
    .sortBy()
    .map((ordinal, index) => ({
      ordinal,
      altitude: index * modifier,
    }))
    .keyBy("ordinal")
    .value()
}

const urlToDataURL = async (url) => {
  const res = await fetch(url, { mode: "cors" })
  const blob = await res.blob()
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result) // e.g. "data:image/png;base64,...."
    reader.readAsDataURL(blob)
  })
}

/**
 * Create a rounded (square→circle) masked SVG data URL using an embedded data-image.
 * Supports separate width / height instead of size.
 *
 * @param {string} imageDataUrl - base64 data URL of the image
 * @param {object} [style={}]
 * @param {number} [style.width=64] - output width (px)
 * @param {number} [style.height=64] - output height (px)
 * @param {number} [style.border=0] - border width (px)
 * @param {string} [style.borderColor='none'] - border & background color
 * @param {string|number} [style.cornerRadius='50%'] - px or % (0 = square, 50% = circle)
 * @returns {string} SVG data URL string
 */
export const createSVGDataURLFromImage = (imageDataUrl, style = {}) => {
  const {
    width = 64,
    height = 64,
    border = 0,
    borderColor = "none",
    cornerRadius = 1,
  } = style

  const innerW = Math.max(0, width - border * 2)
  const innerH = Math.max(0, height - border * 2)
  const maxCorner = Math.min(innerW, innerH) / 2

  const cornerPx =
    cornerRadius <= 1
      ? Math.max(0, cornerRadius) * maxCorner
      : Math.min(cornerRadius, maxCorner)
  const outerCorner = Math.min(cornerPx + border, maxCorner / 2)

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg"
     xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${width}" height="${height}">
  <defs>
    <clipPath id="clip">
      <rect x="${border}" y="${border}" width="${innerW}" height="${innerH}"
            rx="${cornerPx}" ry="${cornerPx}"/>
    </clipPath>
  </defs>

  <!-- background under image -->
  <rect x="0" y="0" width="${width}" height="${height}"
        rx="${outerCorner}" ry="${outerCorner}"
        fill="${borderColor}"/>

  <!-- clipped image -->
  <image x="0" y="0" width="${width}" height="${height}"
         href="${imageDataUrl}" xlink:href="${imageDataUrl}"
         clip-path="url(#clip)" preserveAspectRatio="xMidYMid slice"/>

  <!-- border -->
  <rect x="${border}" y="${border}" width="${innerW}" height="${innerH}"
        rx="${cornerPx}" ry="${cornerPx}"
        fill="none" stroke="${borderColor}" stroke-width="${border}"/>
</svg>`.trim()

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

// Convenience: from remote URL → SVG data URL
const createSVGDataURL = async (imgUrl, style = {}) => {
  const dataImg = await urlToDataURL(imgUrl) // inline it
  return createSVGDataURLFromImage(dataImg, style)
}

const createRoomUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)

  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const altitude = getAltitude(properties)
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}
const createAmenityUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const altitude = getAltitude(properties)
  const area = new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
  return area
}

const createNonpublicUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const altitude = getAltitude(properties)
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}
const createWalkwayUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const altitude = getAltitude(properties)
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}

const createWaterFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const altitude = getAltitude(properties)
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
    zIndex,
  })
}

const createVegetationFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  const altitude = getAltitude(properties)
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: {
      ...symbolStyle,
    },
    zIndex,
  })
}

const createObstructionalFixture = (feature, style = {}, options = {}) => {
  const { geometry, properties = {} } = feature
  const { model3d } = properties
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }
  const isLineString = geometry?.type === "LineString"
  if (!_.isEmpty(model3d)) return
  if (allowOverride) {
    _.merge(symbolStyle, properties.style)
    // If geometry is a LineString (to be converted to Polygon), add polygonFill with lineColor
    if (isLineString) symbolStyle.polygonFill = symbolStyle.lineColor
  }
  if (inheritFillColorToLine) {
    symbolStyle.lineColor = isLineString
      ? symbolStyle.lineColor
      : symbolStyle.polygonFill
  }

  const altitude = getAltitude(properties)
  //create polygon with line-offset if geometry is lineString
  if (isLineString) {
    const polygon = createPolygonFromLineString(geometry)
    return new GeometryType["Polygon"](polygon, {
      properties: {
        altitude: altitude,
        defaultAltitude: altitude,
      },
      symbol: symbolStyle,
      defaultSymbol: symbolStyle,
      zIndex,
    })
  }
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: altitude,
      defaultAltitude: altitude,
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
    zIndex,
  })
}

const creatingDefaultSection = (feature, style = {}, options = {}) => {
  const { geometry, properties, id } = feature

  const {
    allowOverride,
    inheritFillColorToLine,
    zIndex = SECTION_Z_INDEX,
  } = options

  const symbolStyle = { ...style }
  const altitude = getAltitude(properties)

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  try {
    return new GeometryType[geometry.type](geometry.coordinates, {
      properties: {
        id,
        altitude: altitude,
        defaultAltitude: altitude,
      },
      symbol: symbolStyle,
      defaultSymbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.warn(`Error creatingDefaultSection`, geometry.type)
  }
}

const createVegetationSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const creatingSeatingSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const createEatingDrinkingSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const createPrincipalOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }
  const altitude = getAltitude(properties)
  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: altitude,
        defaultAltitude: altitude,
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating pedestrian principal opening:`, feature)
  }
}

const createEmergencyExitOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }
  const altitude = getAltitude(properties)

  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: altitude,
        defaultAltitude: altitude,
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating emergency exit opening:`, feature)
  }
}
const createPedestrianOpening = (feature, style, options = {}) => {
  const { geometry, properties, id } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }
  const altitude = getAltitude(properties)

  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        id,
        feature_type: "opening",
        category: properties.category,
        altitude: altitude,
        defaultAltitude: altitude,
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating pedestrian opening:`, feature)
  }
}

const loadModel3d = (model3d, coordinate, threeLayer) => {
  return new Promise((resolve, reject) => {
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath(
      "https://www.gstatic.com/draco/versioned/decoders/1.5.7/"
    )

    const loader = new GLTFLoader()
    loader.setDRACOLoader(dracoLoader)

    const { url, properties: modelProperties } = model3d
    loader.load(
      url,
      (gltf) => {
        const object3d = gltf.scene
        // จัดขนาด + scale ที่เหมาะสม
        object3d.rotation.x = _.get(modelProperties, "rotation.x")
        object3d.rotation.y = _.get(modelProperties, "rotation.y")
        object3d.scale.set(...(_.get(modelProperties, "scale") || []))
        const object = threeLayer.toModel(object3d, {
          coordinate,
          altitude: modelProperties?.altitude || 0,
        })

        object.getObject3d().traverse((child) => {
          if (child.isMesh === true) {
            child.material.transparent = true
            child.material.metalness = 0.1 // set to near 0 because metal requires environment to reflect light to, if there's nothing then metal will become black.
          }
        })

        resolve(object)
      },
      (xhr) => {},
      (error) => {
        reject(error)
      }
    )
  })
}

const create3DModels = async (
  models,
  defaultCoordinate,
  properties,
  threeLayer
) => {
  let modelObjs = []
  for (let j = 0; j < models.length; j++) {
    const model = models[j]
    const positionCoord = _.get(model, "properties.position")
    const coord = positionCoord || defaultCoordinate
    const object = await loadModel3d(model, coord, threeLayer)
    object.properties = properties
    modelObjs.push(object)
  }

  return modelObjs
}

const createExtrudePolygon = (
  geometry,
  threeLayer,
  material,
  height,
  properties = {},
  /**
   * Extrude Options
   * @see https://maptalks.org/maptalks.three/docs/dist/api/threelayer.html#toextrudepolygon-polygon-polygontype-options-extrudepolygonoptiontype-three-material
   */
  options
) => {
  const { offset = 0, altitude = 0, bottomHeight } = options

  // If the geometry cannot be offset, fallback to the original geometry
  const offsetGeometry =
    turfBuffer(geometry, offset, { units: "meters" }) || feature(geometry)

  const object = threeLayer.toExtrudePolygon(
    offsetGeometry,
    {
      height,
      async: true,
      altitude,
      bottomHeight: bottomHeight ?? 0,
      defaultAltitude: altitude,
    },
    material
  )
  object.properties = properties
  return object
}

const create3DMarker = (
  coordinates,
  options,
  material,
  threeLayer,
  markerProperties
) => {
  return new SpriteMarker(
    coordinates,
    options,
    material,
    threeLayer,
    markerProperties
  )
}

/**
 * Retrieves the extrude configuration for a feature.
 *
 * @param {Object} baseExtrudeConfig - The default extrude configuration defined in the map settings within the app config content type.
 * @param {Object} feature - The target feature object for which the extrude value is required.
 * @returns {Object|undefined} - The extrude value from the feature's `properties` field, or the base extrude configuration as a fallback if not defined.
 */
export const getExtrudeConfigByFeature = (baseExtrudeConfig, feature = {}) => {
  const { feature_type: featureType } = feature
  // Retrieve extrude configuration from the extrude field in the feature's properties
  const featureExtrudeConfig = _.get(feature, "properties.extrude", {})

  // Retrieve extrude configuration from the base extrude settings. (Map Config)
  const featureCategory = _.get(feature, "properties.category")
  const baseFeatureExtrudeConfig = _.get(
    baseExtrudeConfig,
    `${featureType}.${featureCategory || featureType}`,
    {}
  )
  // Fallback to base extrude configuration if the feature's extrude field is not defined.
  const mergedExtrudeConfig = _.merge(
    {},
    baseFeatureExtrudeConfig,
    featureExtrudeConfig
  )
  return isEmpty(mergedExtrudeConfig) ? null : mergedExtrudeConfig
}

const getFeatureMarkerConfig = (feature, mapConfig = {}) => {
  const {
    textMarkerType,
    extrudeConfig = {},
    symbol,
    altitudeModifier = 0,
    altitude,
  } = mapConfig

  const location = getLocationByFeature(feature)
  const featureExtrudeConfig = getExtrudeConfigByFeature(
    extrudeConfig,
    location
  )

  return {
    markerSymbol: symbol,
    textMarkerType,
    featureExtrudeConfig,
    altitudeModifier,
    altitude,
  }
}

export const createSpriteMaterialByLabelSymbol = (labelSymbol = []) => {
  const material = new SpriteMaterial()
  try {
    const [base, icon] = labelSymbol
    const { markerWidth: baseWidth = 24 } = base
    const { markerWidth: iconWidth = 24 } = icon
    const viewBoxDimension = Math.max(baseWidth, iconWidth)

    const baseSVG = createSVGPathFromMarkerSymbol(base)
    const iconSVG = createSVGPathFromMarkerSymbol(icon)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${viewBoxDimension}" height="${viewBoxDimension}">${baseSVG}${iconSVG}</svg>`
    const textureLoader = new TextureLoader()
    const scaleFactor = 200 / 24 // Scale factor to upscale from 24px to 200px to ensure the resolution is high enough

    svgToPng(svg, scaleFactor).then((png) => {
      const texture = textureLoader.load(png, () => {
        material.map = texture
        material.needsUpdate = true
      })
    })
  } catch (error) {
    console.warn(`Error createSpriteMaterialByLabelSymbol: `, labelSymbol)
  }
  return material
}

export const createStyledUIMarkerElement = ({
  style,
  textContent,
  className,
}) => {
  const element = document.createElement("div")
  for (const key in style) {
    element.style[key] = style[key]
  }
  element.className = className
  element.textContent = textContent
  //! Use outerHTML to return HTML string instead of element object to avoid DOM event warnings from Maptalks.js.
  return element.outerHTML
}

export const createStyledUILogoMarkerElement = ({
  logo,
  baseSymbol = {},
  logoSymbol = {},
  className = "",
}) => {
  const baseElement = document.createElement("div")

  for (const key in baseSymbol) {
    baseElement.style[key] = baseSymbol[key]
  }

  const logoElement = document.createElement("img")
  for (const key in logoSymbol) {
    logoElement.style[key] = logoSymbol[key]
  }
  logoElement.alt = "img"
  logoElement.src = logo
  baseElement.appendChild(logoElement)

  baseElement.className = className
  return baseElement.outerHTML
}

export const styledFeatureGenerator = (mapTheme) => {
  const getElementSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.geometry.symbol`)
    if (featureType === key) return featureTypeTheme
    const categoryTheme = _.get(mapTheme, `${key}.geometry.symbol`)
    return _.merge({}, featureTypeTheme, categoryTheme)
  }

  const getLabelSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.label`)
    const categoryTheme = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    let symbols = _.values(_.map(mergedSymbol, "symbol"))
    const markerIndexToMove = symbols.findIndex(
      ({ elementType }) => elementType === "label.marker"
    )

    // Move the label.marker symbol to the last of array
    if (markerIndexToMove >= 0) {
      const markerSymbolToMove = _.pullAt(symbols, markerIndexToMove)[0]
      symbols.push(markerSymbolToMove)
    }

    return symbols
  }

  const getUIMarkerSymbol = (key, namespace) => {
    const namespaceKey = namespace ? `.${namespace}` : ""
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(
      mapTheme,
      `${featureType}.ui.marker${namespaceKey}`
    )
    const categoryTheme = _.get(mapTheme, `${key}.ui.marker${namespaceKey}`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    const symbol = _.get(mergedSymbol, "symbol")
    return symbol
  }

  const getUIMarkerOptions = (key, namespace) => {
    const namespaceKey = namespace ? `.${namespace}` : ""
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(
      mapTheme,
      `${featureType}.ui.marker${namespaceKey}`
    )
    const categoryTheme = _.get(mapTheme, `${key}.ui.marker${namespaceKey}`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    const options = _.get(mergedSymbol, "options")
    return options
  }

  const getLabelOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeSymbol = _.get(mapTheme, `${featureType}.label`)
    const categorySymbol = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeSymbol, categorySymbol)
    return _.reduce(
      mergedSymbol,
      (acc, symbol) => ({ ...acc, ..._.get(symbol, "options", {}) }),
      {}
    )
  }

  const generateSpriteMarkerMaterial = () => {
    return SPRITE_MARKER_FEATURE.reduce((acc, featCate) => {
      const [featureType, category] = featCate.split("-")
      const key = `${featureType}.${category}`
      const labelSymbol = getLabelSymbol(key)
      const material = createSpriteMaterialByLabelSymbol(labelSymbol)
      return { ...acc, [key]: material }
    }, {})
  }

  const generateSpriteHighlightMarkerOption = () => {
    return SPRITE_HIGHLIGHT_MARKER_FEATURE.reduce((acc, featCate) => {
      const categoryKey = _.last(featCate.split("-")) // Example: highlighted-amenity.atm -> amenity.atm
      const defaultLabelSymbol = getLabelSymbol(categoryKey)
      const highlightLabelSymbol = getLabelSymbol(featCate)
      const [defaultBase, defaultIcon] = defaultLabelSymbol
      const [highlightBase, highlightIcon] = highlightLabelSymbol
      const base = _.merge({}, defaultBase, highlightBase)
      const icon = _.merge({}, defaultIcon, highlightIcon)

      const material = createSpriteMaterialByLabelSymbol([base, icon])
      const options = getLabelOptions(featCate)
      return { ...acc, [featCate]: { material, options } }
    }, {})
  }

  const spriteMarkerMaterialObj = generateSpriteMarkerMaterial()
  const spriteHighlightMarkerOptionObj = generateSpriteHighlightMarkerOption()

  const getElementOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeOptions = _.get(
      mapTheme,
      `${featureType}.geometry.options`
    )

    const categoryOptions = _.get(mapTheme, `${key}.geometry.options`)

    return _.merge({}, featureTypeOptions, categoryOptions)
  }

  const createOccupantMarker = (feature, locatedFeature, mapConfig) => {
    const { textMarkerType, featureExtrudeConfig } = getFeatureMarkerConfig(
      feature,
      mapConfig
    )
    const markerHeight = _.get(featureExtrudeConfig, "height", 0)

    const { properties, id: featureId, feature_type } = feature
    if (!properties.anchor) return

    /**
     *  Prepare geometry for occupant marker positioning:
     *  - Use anchor coordinates if the location feature is the main location
     *  - Otherwise, use the center of the location feature
     */
    const locationFeatureId = locatedFeature?.id
    const mainLocationId =
      _.get(properties, "unit.id") || _.get(properties, "kiosk.id")
    const isMainLocationFeature = mainLocationId === locationFeatureId
    const { geometry: mainLocationGeometry } = properties?.anchor
    const geometry = isMainLocationFeature
      ? mainLocationGeometry
      : turfCenter(locatedFeature)?.geometry

    const id = isMainLocationFeature
      ? featureId
      : buildCompositeId([featureId, locationFeatureId], {
          suffix: SUFFIX_MARKER,
        })

    const altitude = getAltitude(properties) + markerHeight
    const baseProperties = {
      id,
      feature_type,
      category: properties.category,
      altitude,
      defaultAltitude: altitude,
    }
    const occupantName = _.isEmpty(properties.short_name)
      ? properties.name
      : properties.short_name

    if (locatedFeature) {
      const { feature_type, properties } = locatedFeature
      const { category } = properties
      const locatedLabelOption =
        feature_type === "kiosk"
          ? getLabelOptions(`${feature_type}`)
          : getLabelOptions(`${feature_type}.${category}`)
      const { hidden = false } = locatedLabelOption || {}
      if (hidden) return
    }

    const getValidatedRenderType = (requestedType, logoUrl) => {
      // If requestType is "Logo" or "Logo + Name" and no logo, fallback to "Name"
      if (requestedType === "Logo" || requestedType === "Logo + Name") {
        return logoUrl ? requestedType : "Name"
      }
      return requestedType
    }

    const venueReference = _.get(properties, "venue.properties.reference", "")

    const logoUrl = _.get(properties, "logo.url")
    const requestedRenderType = _.get(properties, "render_type", "Name")
    const renderType = getValidatedRenderType(requestedRenderType, logoUrl)
    if (renderType === "Label") return null

    const renderPriority = properties.render_priority || 3
    const labelSymbol = getLabelSymbol(`occupant-${_.toLower(renderType)}`)
    const markerSymbol = _.last(labelSymbol)

    const coordinates = _.get(geometry, "coordinates")

    const priorityLabelSymbol = getLabelSymbol(
      `occupant-${_.toLower(renderType)}-${renderPriority}`
    )
    const priorityMarkerSymbol = _.last(priorityLabelSymbol) || {}

    switch (renderType) {
      case "Logo":
        _.set(priorityMarkerSymbol, "markerFile", logoUrl)

        return new Marker(coordinates, {
          properties: {
            ...baseProperties,
          },
          symbol: priorityLabelSymbol,
        })

      case "Logo + Name":
        return new Marker(coordinates, {
          properties: {
            name: occupantName.en,
            ...baseProperties,
          },
          symbol: {
            textName: "{name}",
            markerFile: logoUrl,
            textHaloRadius: {
              stops: [
                [20, 2],
                [21, 3],
              ],
            },
            ...markerSymbol,
            ...priorityMarkerSymbol,
          },
        })
      case "None":
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style: { display: "none" },
            textContent: occupantName.en,
          }),
          altitude,
        })
      case "Name":
      default:
        if (textMarkerType === "marker") {
          return new Marker(coordinates, {
            properties: {
              name: occupantName.en,
              ...baseProperties,
            },
            symbol: {
              textName: "{name}",
              //TODO:: implement symbol scaler later
              textHaloRadius: {
                stops: [
                  [20, 1],
                  [21, 2],
                ],
              },
              ...markerSymbol,
              ...priorityMarkerSymbol,
            },
          })
        }

        const symbolTypeKey = !_.isEmpty(venueReference)
          ? `occupant-${_.toLower(renderType)}-${venueReference}`
          : `occupant-${_.toLower(renderType)}`

        const uiMarkerSymbol = getUIMarkerSymbol(symbolTypeKey)

        const uiMarkerPrioritySymbol = getUIMarkerSymbol(
          `occupant-${_.toLower(renderType)}-${renderPriority}`
        )
        const uiMarkerPriorityOptions = getUIMarkerOptions(
          `occupant-${_.toLower(renderType)}-${renderPriority}`
        )

        const style = { ...uiMarkerSymbol, ...uiMarkerPrioritySymbol }
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style,
            textContent: occupantName.en,
            className: OCCUPANT_TEXT_MARKER_CLASSNAME,
          }),
          collision: true,
          collisionFadeIn: true,
          altitude,
          ...uiMarkerPriorityOptions,
        })
    }
  }

  const createSectionMarker = async (feature, locatedFeature, mapConfig) => {
    const { textMarkerType, featureExtrudeConfig } = getFeatureMarkerConfig(
      feature,
      mapConfig
    )
    const markerHeight = _.get(featureExtrudeConfig, "height", 0)
    const { properties, id, feature_type } = feature

    const displayPoint = _.get(feature, "properties.display_point")
    const coordinates = getCenterFromGeometry(displayPoint || feature.geometry)

    if (!coordinates) return

    const altitude = getAltitude(properties) + markerHeight
    const baseProperties = {
      id: buildCompositeId([id], {
        suffix: SUFFIX_MARKER,
      }),
      feature_type,
      category: properties.category,
      altitude,
    }
    const sectionName = _.isEmpty(properties.short_name)
      ? properties.name
      : properties.short_name

    if (locatedFeature) {
      const { feature_type, properties } = locatedFeature
      const { category } = properties
      const locatedLabelOption =
        feature_type === "kiosk"
          ? getLabelOptions(`${feature_type}`)
          : getLabelOptions(`${feature_type}.${category}`)
      const { hidden = false } = locatedLabelOption || {}
      if (hidden) return
    }

    const getValidatedRenderType = (requestedType, logoUrl) => {
      // If requestType is "Logo" or "Logo + Name" and no logo, fallback to "Name"
      if (requestedType === "Logo" || requestedType === "Logo + Name") {
        return logoUrl ? requestedType : "Name"
      }
      return requestedType
    }

    const venueReference = _.get(properties, "venue.properties.reference", "")
    const logoUrl = _.get(properties, "logo.url")
    const requestedRenderType = _.get(properties, "render_type", "Name")
    const renderType = getValidatedRenderType(requestedRenderType, logoUrl)
    if (renderType === "Label") return null

    const renderPriority = properties.render_priority || 3
    const labelSymbol = getLabelSymbol(`section-${_.toLower(renderType)}`)

    const markerSymbol = _.last(labelSymbol)

    const priorityLabelSymbol = getLabelSymbol(
      `section-${_.toLower(renderType)}-${renderPriority}`
    )
    const priorityMarkerSymbol = _.last(priorityLabelSymbol) || {}

    switch (renderType) {
      case "Logo": {
        const svgMarker = await createSVGDataURL(logoUrl, priorityMarkerSymbol)
        _.set(priorityMarkerSymbol, "markerFile", svgMarker)
        return new Marker(coordinates, {
          properties: {
            ...baseProperties,
          },
          symbol: priorityLabelSymbol,
        })
      }

      case "Logo + Name": {
        const svgMarker = await createSVGDataURL(logoUrl, priorityMarkerSymbol)
        return new Marker(coordinates, {
          properties: {
            name: sectionName.en,
            ...baseProperties,
          },
          symbol: {
            textName: "{name}",
            markerFile: svgMarker,
            textHaloRadius: {
              stops: [
                [20, 1],
                [21, 2],
              ],
            },
            ...markerSymbol,
            ...priorityMarkerSymbol,
          },
        })
      }
      case "None":
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style: { display: "none" },
            textContent: sectionName.en,
          }),
          altitude,
        })
      case "Name":
      default:
        if (textMarkerType === "marker") {
          return new Marker(coordinates, {
            properties: {
              name: sectionName.en,
              ...baseProperties,
            },
            symbol: {
              textName: "{name}",
              //TODO:: implement symbol scaler later
              textHaloRadius: {
                stops: [
                  [20, 1],
                  [21, 2],
                ],
              },
              ...markerSymbol,
              ...priorityMarkerSymbol,
            },
          })
        }

        const symbolTypeKey = !_.isEmpty(venueReference)
          ? `section-${_.toLower(renderType)}-${venueReference}`
          : `section-${_.toLower(renderType)}`

        const uiMarkerSymbol = getUIMarkerSymbol(symbolTypeKey)
        const uiMarkerPrioritySymbol = getUIMarkerSymbol(
          `section-${_.toLower(renderType)}-${renderPriority}`
        )
        const uiMarkerPriorityOptions = getUIMarkerOptions(
          `section-${_.toLower(renderType)}-${renderPriority}`
        )

        const style = { ...uiMarkerSymbol, ...uiMarkerPrioritySymbol }
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style,
            textContent: sectionName.en,
            className: SECTION_TEXT_MARKER_CLASSNAME,
          }),
          collision: true,
          collisionFadeIn: true,
          altitude,
          ...uiMarkerPriorityOptions,
        })
    }
  }

  const getFeatureProperties = (feature) => {
    const { id, feature_type, properties, geometry } = feature
    const { category, ordinal, style = {}, venue } = properties
    const { reference = "" } = venue?.properties || {}
    const elementStyle = getElementSymbol(`${feature_type}.${category}`)
    const { allowOverride } = getElementOptions(`${feature_type}.${category}`)
    if (allowOverride) _.merge(elementStyle, style)
    const { polygonFill, lineColor } = elementStyle
    const color = geometry?.type === "LineString" ? lineColor : polygonFill
    const featureProperty = {
      id,
      feature_type,
      category: category || feature_type,
      ordinal,
      defaultColor: color,
      venueReference: reference,
    }
    return featureProperty
  }

  return {
    getElementSymbol,
    getHilighPolygonalSymbol: (type, venueReference = "") => {
      switch (type) {
        case "MultiPolygon":
        case "Polygon": {
          const featureType = !_.isEmpty(venueReference)
            ? `hilight-polygonal-${venueReference}`
            : "hilight-polygonal"
          return getElementSymbol(featureType)
        }
        case "Point": {
          return getElementSymbol("hilight-marker")
        }
        default:
          break
      }
    },
    getHighlightMarkerSymbol: () => {
      return getElementSymbol("highlighted-marker")
    },
    createVenue: (feature) => {
      const { geometry, feature_type } = feature
      const symbolStyle = getElementSymbol(feature_type)
      return new GeometryType[geometry.type](geometry.coordinates, {
        symbol: {
          ...symbolStyle,
        },
        zIndex: VENUE_Z_INDEX,
      })
    },
    createFootprint: (feature) => {
      const { geometry, id, properties, feature_type } = feature
      const { category } = properties
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)

      const { allowOverride, inheritFillColorToLine } = options
      const symbolStyle = { ...elementStyle }
      if (allowOverride) _.merge(symbolStyle, properties.style)

      if (inheritFillColorToLine)
        symbolStyle.lineColor = symbolStyle.polygonFill

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          id,
          feature_type: "footprint",
          category: category,
        },
        symbol: symbolStyle,
        zIndex: FOOTPRINT_Z_INDEX,
      })
    },
    createLevel: (feature) => {
      const { geometry, properties, feature_type } = feature
      const { category } = properties

      const style = getElementSymbol(`${feature_type}.${category}`)
      // const options = getElementOptions(`${feature_type}.${category}`)

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          altitude: getAltitude(properties),
        },
        symbol: {
          ...style,
        },
        zIndex: LEVEL_Z_INDEX,
      })
    },

    createUnit: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)
      const zIndex = UNIT_Z_INDEX
      switch (feature.properties.category) {
        case "room":
        case "terrace":
        case "unenclosedarea":
        case "vegetation":
        case "unspecified":
        case "recreation":
          return createRoomUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "firstaid":
        case "restroom":
        case "restroom.female":
        case "restroom.male":
        case "escalator":
        case "elevator":
        case "stairs":
        case "stairs.emergencyexit":
        case "parking":
        case "smokingarea":
        case "mothersroom":
        case "privatelounge":
        case "shuttle":
        case "fieldofplay":
          return createAmenityUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "nonpublic":
        case "structure":
        case "brick":
        case "concrete":
        case "drywall":
        case "glass":
        case "wood":
        case "column":
        case "opentobelow":
          return createNonpublicUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "walkway":
        case "steps":
        case "footbridge":
          return createWalkwayUnit(feature, elementStyle, options).setZIndex(
            zIndex - 1
          )
        default:
          break
      }
    },

    createMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const markerSymbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          symbol: markerSymbol,
        })
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },
    createOriginMarker: (feature) => {
      const { id, geometry, properties } = feature
      const coordinates = getCenterFromGeometry(geometry)
      const markerSymbol = getElementSymbol("origin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          id,
          symbol: markerSymbol,
        })
      } catch (error) {
        console.log(`error creating origin marker:`, feature)
      }
    },
    createDestinationPinMarker: (feature, mapConfig) => {
      const { featureExtrudeConfig } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height")
      // Get Label from located unit / kiosk
      const { id, properties } = feature
      const geometry =
        _.get(feature, "geometry") ||
        _.get(feature, "properties.anchor.geometry")
      const coordinates = getCenterFromGeometry(geometry)
      const symbol = getElementSymbol("pin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
          },
          id,
          symbol,
        })
      } catch (error) {
        console.log(`error creating destination marker:`, feature)
      }
    },
    createDestinationLogoMarker: (feature, mapConfig = {}) => {
      const { featureExtrudeConfig } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height", 0)

      // Get Label from located unit / kiosk
      const { properties, id } = feature
      const { geometry } = properties.anchor
      const logoUrl = _.get(properties, "logo.url")
      const coordinates = getCenterFromGeometry(geometry)
      const [markerBase, markerLabel] = getLabelSymbol(
        logoUrl ? "highlighted-logo-marker" : "default-highlighted-logo-marker"
      )
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
          },
          id,
          symbol: [
            markerBase,
            { ...markerLabel, ...(logoUrl ? { markerFile: logoUrl } : {}) },
          ],
        })
      } catch (error) {
        console.log(`error creating destination marker:`, feature)
      }
    },
    createUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle.start,
          zIndex: USER_LOCATION_Z_INDEX,
        })

        marker.animate(
          {
            symbol: symbolStyle.end,
          },
          {
            repeat: true,
            easing: "upAndDown",
            duration: 1500,
          }
        )
        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createLastUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("last-user-location") || {}
        const options = getElementOptions("last-user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            ...options,
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle,
          zIndex: LAST_LOCATION_Z_INDEX,
        })

        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createHighlightOccupantMarker: (feature, mapConfig) => {
      const { featureExtrudeConfig, markerSymbol } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height")
      const { id, feature_type, properties } = feature

      if (!properties.anchor) return

      const markerProperties = {
        ...properties,
        id,
        feature_type,
        altitude: getAltitude(properties) + markerHeight,
      }

      const { geometry } = properties?.anchor
      const coordinates = _.get(geometry, "coordinates")
      const logoUrl = _.get(properties, "logo.url")

      // If the symbol option is defined, create a marker.
      if (markerSymbol) {
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol: markerSymbol,
        })
      }

      //use pin-marker if no logo
      if (!logoUrl) {
        const symbol =
          getLabelSymbol("default-highlight-occupant-logo") ||
          getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol,
        })
      }

      const labelSymbol = getLabelSymbol("highlight-occupant-logo")
      const priorityMarkerSymbol = _.last(labelSymbol) || {}
      _.set(priorityMarkerSymbol, "markerFile", logoUrl)
      return new Marker(coordinates, {
        properties: markerProperties,
        symbol: labelSymbol,
      })
    },
    createHighlightSectionMarker: async (feature, mapConfig) => {
      const { featureExtrudeConfig, markerSymbol, altitudeModifier, altitude } =
        getFeatureMarkerConfig(feature, mapConfig)

      const markerHeight = _.get(featureExtrudeConfig, "height", 0)
      const { id, feature_type, properties } = feature

      const displayPoint = _.get(feature, "properties.display_point")
      const coordinates = getCenterFromGeometry(
        displayPoint || feature.geometry
      )
      if (!coordinates) return

      const markerProperties = {
        ...properties,
        id,
        feature_type,
        altitude:
          (isFinite(altitude)
            ? altitude
            : getAltitude(properties, altitudeModifier)) + markerHeight,
      }

      const logoUrl = _.get(properties, "logo.url")

      // If the symbol option is defined, create a marker.
      if (markerSymbol) {
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol: markerSymbol,
        })
      }

      //use pin-marker if no logo
      if (!logoUrl) {
        const symbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol,
        })
      }

      const labelSymbol = getLabelSymbol("highlight-section-logo")
      const priorityMarkerSymbol = _.last(labelSymbol) || {}
      const svgMarker = await createSVGDataURL(logoUrl, priorityMarkerSymbol)
      _.set(priorityMarkerSymbol, "markerFile", svgMarker)
      return new Marker(coordinates, {
        properties: markerProperties,
        symbol: labelSymbol,
      })
    },
    createHighlight2DAmenityMarkerFrom3DMarker: (feature, mapConfig) => {
      const { coordinates, units, kiosk } = feature
      const amenityLocatedUnit = _.first(units)

      const unitConfig = getExtrudeConfigByFeature(
        mapConfig,
        amenityLocatedUnit
      )
      const unitHeight = _.get(unitConfig, "height", 0)
      const kioskConfig = getExtrudeConfigByFeature(mapConfig, kiosk)
      const kioskHeight = _.get(kioskConfig, "height", 0)

      const markerHeight = unitHeight + kioskHeight

      const symbol = getElementSymbol("highlight-amenity-marker")
      return new Marker(coordinates, {
        properties: {
          ...feature,
          altitude: markerHeight,
        },
        symbol,
      })
    },
    createKiosk: (feature) => {
      const { geometry, feature_type, properties, id } = feature
      const {
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(
        `${feature_type}.${category}.geometry.options`
      )

      const { allowOverride, inheritFillColorToLine } = options

      const symbolStyle = { ...elementStyle }

      if (allowOverride) _.merge(symbolStyle, properties.style)
      if (inheritFillColorToLine)
        symbolStyle.lineColor = symbolStyle.polygonFill

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          id,
          feature_type,
          category: "kiosk",
          altitude: getAltitude(properties),
        },
        symbol: symbolStyle,
        defaultSymbol: symbolStyle,
        zIndex: KIOSK_Z_INDEX,
      })
    },

    createSection: (feature) => {
      const { properties, feature_type } = feature
      const { category } = properties
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: SECTION_Z_INDEX,
      }

      switch (category) {
        case "eatingdrinking":
          return createEatingDrinkingSection(feature, elementStyle, options)
        case "vegetation":
          return createVegetationSection(feature, elementStyle, options)
        case "seating":
          return creatingSeatingSection(feature, elementStyle, options)
        default:
          return creatingDefaultSection(feature, elementStyle, options)
      }
    },
    createSectionMarker,
    createOccupant: (feature, location, mapConfig) => {
      return createOccupantMarker(feature, location, mapConfig)
    },

    createOpening: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: OPENING_Z_INDEX,
      }
      switch (feature.properties.category) {
        case "pedestrian":
          return createPedestrianOpening(feature, elementStyle, options)
        case "pedestrian.principal":
          return createPrincipalOpening(feature, elementStyle, options)
        case "emergencyexit":
          return createEmergencyExitOpening(feature, elementStyle, options)
        default:
          break
      }
    },

    createFixture: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: FIXTURE_Z_INDEX,
      }
      switch (feature.properties.category) {
        case "water":
          return createWaterFixture(feature, elementStyle, options)
        case "vegetation":
          return createVegetationFixture(feature, elementStyle, options)
        case "equipment":
        case "obstruction":
        case "stage":
        case "desk":
        case "wall":
          return createObstructionalFixture(feature, elementStyle, options)
        default:
          break
      }
    },

    createLineStringFromGeometries: (geometries) => {
      const mergedCoordinates = _(geometries)
        .map((geometry) => {
          switch (geometry.type) {
            case "Point":
              return [
                geometry?.getCoordinates
                  ? geometry?.getCoordinates()
                  : new Coordinate(geometry.coordinates),
              ]
            case "Polygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new Polygon(geometry.coordinates).getCenter(),
              ]
            case "MultiPolygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new MultiPolygon(geometry.coordinates).getCenter(),
              ]
            case "LineString":
            default:
              return geometry?.getCoordinates
                ? geometry?.getCoordinates()
                : new LineString(geometry.coordinates).getCoordinates()
          }
        })
        .flatten()
        .value()
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const startPathSymbolMarker = getElementSymbol("navigation-path-start")

      const line = new LineString(mergedCoordinates, {
        smoothness: 0.5,
        symbol: [...stepPathLineSymbol, startPathSymbolMarker],
      })

      return line
    },
    create3DStepPath: (feature, threeLayer, option = {}) => {
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const pathLineSymbol = stepPathLineSymbol[1]
      const pathLineEffect = stepPathLineSymbol[0]
      const lineOptions = {
        color: pathLineSymbol?.lineColor,
        opacity: pathLineSymbol?.lineOpacity,
      }
      const outlineOptions = {
        color: pathLineEffect?.lineColor,
        opacity: pathLineEffect?.lineOpacity,
      }

      return new NavigationPath(
        feature,
        threeLayer,
        getFeatureProperties(feature),
        option,
        lineOptions,
        outlineOptions
      )
    },

    createDecoration: (decoration, options) => {
      const { type, coordinates } = decoration
      const { id, ordinal, symbol } = options
      const formattedProperties = {
        properties: {
          id,
          altitude: getAltitude(options),
          ordinal,
        },
        symbol,
        zIndex: DECORATION_Z_INDEX,
      }
      switch (type) {
        case "Polygon":
          return new Polygon(coordinates, formattedProperties)
        case "MultiPolygon":
          return new MultiPolygon(coordinates, formattedProperties)
        case "LineString":
          return new LineString(coordinates, formattedProperties)
        case "MultiLineString":
          return new MultiLineString(coordinates, formattedProperties)
        default:
          return null
      }
    },

    /** Three JS */
    create3DFootprint: async (feature, threeLayer, options) => {
      const objects = []
      const { height } = options || {}
      if (!height) return objects

      const { properties } = feature
      const footprintProperties = getFeatureProperties(feature)

      // Use properties.model3d if exists
      const hasModel3ds =
        Array.isArray(properties.model3d) && properties.model3d.length > 0
      if (hasModel3ds) {
        const models = properties.model3d
        const center = turfCenter(feature)
        const coordinate = _.get(center, "geometry.coordinates")
        for (const model of models) {
          const object = await loadModel3d(model, coordinate, threeLayer)
          object.properties = footprintProperties
          objects.push(object)
        }
      } else {
        const color = footprintProperties.defaultColor
        //skip create extrude polygon if color === "transparent" because js can't convert color:"transparent" to material color
        if (color === "transparent") return
        const material = new MeshLambertMaterial({
          color,
          transparent: true,
        })
        const object = createExtrudePolygon(
          feature.geometry,
          threeLayer,
          material,
          height,
          footprintProperties,
          {}
        )
        objects.push(object)
      }

      return objects
    },
    create3DGroundLabel: (label, threeLayer) => {
      const text = label.properties.name
      const bound = label.geometry.coordinates[0]
      if (_.isNil(bound)) throw new Error("Invalid coordinates")

      // Retrieve label symbol from map theme
      const groundLabelSymbol = getElementSymbol("ground-label")

      // Retrieve label symbol from label feature properties
      const featureSymbol = _.get(label, "properties", {})

      // Apply default style from map theme if not defined in label feature
      const groundLabelOptions = _.merge(
        {},
        { text },
        groundLabelSymbol,
        featureSymbol
      )

      return new GroundLabel(bound, groundLabelOptions, threeLayer)
    },

    createOccupantGroundLabel: (feature, location, mapConfig, threeLayer) => {
      const text =
        feature.properties.short_name?.en || feature.properties.name.en
      const bound = location.geometry.coordinates[0]
      if (_.isNil(bound)) throw new Error("Invalid coordinates")

      const groundLabelSymbol = getElementSymbol("occupant-flat-label")
      const groundLabelOptions = getElementOptions("occupant-flat-label")

      const baseAltitude = getAltitude(location.properties)
      const extrudeHeight = _.get(
        mapConfig,
        "extrudeConfig.unit.room.height",
        0
      )
      const totalAltitude = baseAltitude + extrudeHeight + 0.05

      const customAngle = _.get(feature, "properties.style.angle")
      const offsetX = _.get(feature, "properties.style.offsetX", 0)
      const offsetY = _.get(feature, "properties.style.offsetY", 0)

      const featureSymbol = {
        name: text,
        ordinal: location.properties.ordinal,
        venue_id: feature.properties.venue_id,
      }

      // symbol, options and custom properties
      const mergedGroundLabelOptions = _.merge(
        {},
        { text },
        groundLabelSymbol,
        groundLabelOptions,
        featureSymbol,
        {
          altitude: totalAltitude,
          offsetX,
          offsetY,
          // set custom angle
          ...(!_.isNil(customAngle) ? { angle: customAngle } : {}),
        }
      )

      const groundLabel = new GroundLabel(
        bound,
        mergedGroundLabelOptions,
        threeLayer
      )
      return groundLabel
    },

    create3DBillboard: (billboard, threeLayer) => {
      const { id, feature_type, properties } = billboard
      const {
        logo,
        altitude,
        scale,
        alphaTest,
        legColor,
        showLeg,
        is_clickable,
      } = properties
      const coordinates = getCenterFromGeometry(billboard.geometry)
      const billboardProperties = {
        ...billboard.properties,
        id,
        feature_type,
      }

      const options = {
        altitude,
        scale,
        alphaTest,
        legColor,
        showLeg,
        interactive: is_clickable,
      }

      return new Billboard(
        coordinates,
        options,
        logo?.url,
        threeLayer,
        billboardProperties
      )
    },
    create3DAmenityMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, units, kiosk, is_clickable } = properties
      const amenityLocatedUnit = _.first(units)
      const isLocatedUnitModel3dAvailable = !_.isEmpty(
        amenityLocatedUnit?.properties?.model3d
      )
      const isLocatedKioskModel3dAvailable = !_.isEmpty(
        kiosk?.properties?.model3d
      )

      const unitConfig = getExtrudeConfigByFeature(config, amenityLocatedUnit)
      const unitHeight = isLocatedUnitModel3dAvailable
        ? 0
        : _.get(unitConfig, "height", 0)
      const kioskConfig = getExtrudeConfigByFeature(config, kiosk)
      const kioskHeight = isLocatedKioskModel3dAvailable
        ? 0
        : _.get(kioskConfig, "height", 0)

      const coordinates = _.get(geometry, "coordinates")

      const markerProperties = {
        ...properties,
        coordinates,
        id,
        feature_type,
      }

      const material = _.get(
        spriteMarkerMaterialObj,
        `${feature_type}.${category}`
      )

      const highlightOptions = _.get(
        spriteHighlightMarkerOptionObj,
        `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
      )

      const options = {
        scale: 0.05,
        altitude: unitHeight + kioskHeight,
        highlight: highlightOptions,
        interactive: is_clickable,
      }

      return create3DMarker(
        coordinates,
        options,
        material,
        threeLayer,
        markerProperties
      )
    },

    create3DOccupantAmenityMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, unit, kiosk } = properties
      const amenityLocation = kiosk || unit
      const locationConfig = getExtrudeConfigByFeature(config, amenityLocation)
      const coordinates = _.get(geometry, "coordinates")

      const markerProperties = {
        ...properties,
        coordinates,
        id,
        feature_type,
      }

      const material = _.get(
        spriteMarkerMaterialObj,
        `${feature_type}.${category}`
      )

      const highlightOptions = _.get(
        spriteHighlightMarkerOptionObj,
        `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
      )

      const options = {
        scale: 0.05,
        altitude: _.get(locationConfig, "height", 0),
        highlight: highlightOptions,
      }

      return create3DMarker(
        coordinates,
        options,
        material,
        threeLayer,
        markerProperties
      )
    },

    create3DOpeningMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, unit, kiosk } = properties
      const amenityLocation = kiosk || unit
      const locationConfig = getExtrudeConfigByFeature(config, amenityLocation)
      const coordinates = _.get(geometry, "coordinates")

      const markerProperties = {
        ...properties,
        coordinates,
        id,
        feature_type,
      }

      const material = _.get(
        spriteMarkerMaterialObj,
        `${feature_type}.${category}`
      )

      const highlightOptions = _.get(
        spriteHighlightMarkerOptionObj,
        `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
      )

      const options = {
        scale: 0.05,
        altitude: _.get(locationConfig, "height", 0),
        highlight: highlightOptions,
      }

      return create3DMarker(
        coordinates,
        options,
        material,
        threeLayer,
        markerProperties
      )
    },
    create3DUnit: async (unit, threeLayer) => {
      const { id, feature_type, properties } = unit
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(unit)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },

    create3DKiosk: async (kiosk, threeLayer) => {
      const { id, feature_type, properties } = kiosk
      const { ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        ordinal,
      }

      const center = turfCenter(kiosk)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },

    createVenue3DModel: async (venue, threeLayer) => {
      const { id, feature_type, properties } = venue
      const { category, model3d } = properties
      const modelProperty = {
        id,
        feature_type,
        category,
      }
      const center = turfCenter(venue)
      const centerCoord = _.get(center, "geometry.coordinates")
      const modelPosition = _.get(model3d, "properties.position", centerCoord)
      const models = await create3DModels(
        model3d,
        modelPosition,
        modelProperty,
        threeLayer
      )
      return models
    },

    create3DFixture: async (fixture, threeLayer) => {
      const { id, feature_type, properties } = fixture
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(fixture)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },
    createExtrudedUnit: (unit, threeLayer, options) => {
      const { height, altitude, bottomHeight } = options || {}
      if (!height) return

      //NOTE: this is temporary condition that only extrude when the unit is "room"
      const unitProperty = getFeatureProperties(unit)
      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        // TODO: Move to extrude config later
        offset: -0.1,
        altitude: altitude ?? 0,
        bottomHeight: bottomHeight ?? 0,
      }
      const color = unitProperty.defaultColor

      if (color === "transparent") return

      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })
      //create polygon with line-offset if geometry is lineString
      if (unit.geometry.type === "LineString") {
        const polygon = createPolygonFromLineString(unit.geometry)
        const geometry = {
          type: "Polygon",
          coordinates: polygon,
        }
        return createExtrudePolygon(
          geometry,
          threeLayer,
          material,
          height,
          unitProperty,
          options3d
        )
      }
      const object = createExtrudePolygon(
        unit.geometry,
        threeLayer,
        material,
        height,
        unitProperty,
        options3d
      )
      return object
    },
    createExtrudedKiosk: (kiosk, threeLayer, options) => {
      const { height, altitude, bottomHeight } = options || {}
      if (!height) return
      const kioskProperty = getFeatureProperties(kiosk)
      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        offset: -0.1,
        altitude: altitude ?? 0,
        bottomHeight: bottomHeight ?? 0,
      }
      const color = kioskProperty.defaultColor
      if (color === "transparent") return
      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })
      const object = createExtrudePolygon(
        kiosk.geometry,
        threeLayer,
        material,
        height,
        kioskProperty,
        options3d
      )
      return object
    },
    createExtrudedLevel: (level, threeLayer, options) => {
      const { height } = options || {}
      if (!height) return

      const levelProperty = getFeatureProperties(level)
      const options3d = { offset: 0 }
      const color = levelProperty?.defaultColor || "#808080"
      if (color === "transparent" || _.isEmpty(level?.geometry?.coordinates))
        return
      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })
      const object = createExtrudePolygon(
        level.geometry,
        threeLayer,
        material,
        height,
        levelProperty,
        options3d
      )
      return object
    },
    createExtrudedSection: (section, threeLayer, options) => {
      const { height, altitude } = options || {}
      if (!height) return

      const sectionProperty = getFeatureProperties(section)

      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        offset: -0.1,
        altitude: altitude ?? 0,
      }
      const color = sectionProperty.defaultColor
      if (color === "transparent") return
      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })
      const object = createExtrudePolygon(
        section.geometry,
        threeLayer,
        material,
        height,
        sectionProperty,
        options3d
      )
      return object
    },
    createAmbientLight: (config) => {
      const { color: colorString = "0xffffff", intensity = 1 } = config
      const color = parseInt(colorString, 16)
      const ambientLight = new AmbientLight(color, intensity)
      return ambientLight
    },
    createDirectionalLight: (config) => {
      const {
        color: colorString = "0xffffff",
        intensity = 1,
        position: positionString = [0, 0, 0],
      } = config
      const color = parseInt(colorString, 16)
      const [x, y, z] = positionString
      const light = new DirectionalLight(color, intensity)
      light.position.set(x, y, z).normalize()
      return light
    },
    create3DModels,
  }
}

const EXCEPT_AMENITY_LOCATION_CATEGORIES = [
  "walkway",
  "nonpublic",
  "parking",
  "opentobelow",
  "unspecified",
]
export const getLocationByAmenity = (feature) => {
  const unit = _.get(feature, "properties.units[0]", null)
  const unitCategory = _.get(unit, "properties.category")
  if (!unit) return feature.id
  return EXCEPT_AMENITY_LOCATION_CATEGORIES.includes(unitCategory)
    ? feature
    : unit
}

export const getLocationByOccupant = (feature) => {
  const kiosk = _.get(feature, "properties.kiosk", null)
  const unit = _.get(feature, "properties.anchor.properties.unit", null)
  return kiosk || unit
}

export const getLocationBySection = (feature) => {
  const kiosk = _.get(feature, "properties.kiosk", null)
  const unit = _.get(feature, "properties.unit", null)
  return kiosk || unit
}

export const getLocationIdByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "kiosk":
    case "unit":
    case "opening":
      return feature.id
    case "amenity":
      return getLocationByAmenity(feature)?.id
    case "occupant":
      return getLocationByOccupant(feature)?.id
    case "section":
      return feature?.properties?.unit_id
    case "promotion":
    case "event":
      return feature.properties?.feature_id
    default:
      return
  }
}

export const getFeatureByLocationId = (id, features = []) => {
  return features?.find((f) => {
    const mainLocationId = getLocationIdByFeature(f)
    const relatedLocationIds = getRelatedLocationIdsByFeature(f)
    return mainLocationId === id || relatedLocationIds.includes(id)
  })
}

export const getFeaturesByLocationId = (id, features = []) => {
  return features?.filter((f) => {
    const mainLocationId = getLocationIdByFeature(f)
    const relatedLocationIds = getRelatedLocationIdsByFeature(f)
    return mainLocationId === id || relatedLocationIds.includes(id)
  })
}

export const isClickableFeature = (feature) => {
  const isClickable = _.get(feature, "properties.is_clickable")
  switch (feature?.feature_type) {
    case "amenity":
      return _.isNull(isClickable) ? true : isClickable
    case "occupant":
      return true
    default:
      return
  }
}

export const getLocationByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getLocationByAmenity(feature)
    case "occupant":
      return getLocationByOccupant(feature)
    case "section":
      return getLocationBySection(feature)
    default:
      return
  }
}
export const getSectionByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "occupant":
    case "unit":
    case "kiosk":
      return feature?.properties.section
    default:
      return
  }
}

export const getMarkerId = (marker) => {
  // Retrieve marker ID via `properties.id` for Marker, or `options.properties.id` for UIMarker.
  return marker?.properties?.id || marker?.options?.properties?.id
}

// Main feature ID is always the first item.
export const parseElementId = (id) => id?.split("_")[0]

/*
 * Utility functions to retrieve related locations for amenities and occupants.
 * - Used for highlighting on the floor plan.
 * - Displays location details.
 */

export const getRelatedLocationsByOccupant = (feature) => {
  const kiosks = _.get(feature, "properties.kiosks", [])
  const units = _.get(feature, "properties.units", [])
  return [...kiosks, ...units]
}

export const getRelatedLocationsBySection = (feature) => {
  const kiosks = _.get(feature, "properties.kiosks", [])
  const units = _.get(feature, "properties.units", [])
  const fixtures = _.get(feature, "properties.fixtures", [])
  const amenities = _.get(feature, "properties.amenities", [])
  return [...kiosks, ...units, ...fixtures, ...amenities]
}

export const getRelatedLocationsByAmenity = (feature) => {
  const units = _.get(feature, "properties.units", [])

  if (units.length === 0) return [feature]

  return units.filter((unit) => {
    const unitCategory = _.get(unit, "properties.category")
    return !EXCEPT_AMENITY_LOCATION_CATEGORIES.includes(unitCategory)
  })
}

export const getRelatedLocationIdsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getRelatedLocationsByAmenity(feature).map((v) => v?.id)
    case "occupant":
      return getRelatedLocationsByOccupant(feature).map((v) => v?.id)
    case "section":
      return getRelatedLocationsBySection(feature).map((v) => v?.id)
    default:
      return []
  }
}

export const getPossibleMarkerIdsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "section":
      return [
        buildCompositeId([feature.id], {
          suffix: SUFFIX_MARKER,
        }),
      ]
    default:
      return []
  }
}

export const getDuplexGeometryIdsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "occupant":
      return getRelatedLocationsByOccupant(feature).map((location) =>
        buildCompositeId([feature?.id, location?.id], {
          suffix: SUFFIX_MARKER,
        })
      )
    default:
      return []
  }
}

export const getRelatedLocationsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getRelatedLocationsByAmenity(feature)
    case "occupant":
      return getRelatedLocationsByOccupant(feature)
    case "section":
      return getRelatedLocationsBySection(feature)
    default:
      return []
  }
}

export const getOrdinalByLocationId = (locationId, feature) => {
  if (!feature) return null

  const mainUnit = _.get(feature, "properties.unit")
  const mainKiosk = _.get(feature, "properties.kiosk")
  const relatedLocations = getRelatedLocationsByFeature(feature)

  const allLocations = [mainUnit, mainKiosk, ...relatedLocations].filter(
    Boolean
  )
  const targetLocation = allLocations.find(
    (location) => location.id === locationId
  )

  return targetLocation
    ? _.get(targetLocation, "properties.level.properties.ordinal") ||
        _.get(targetLocation, "properties.ordinal")
    : null
}
